import React, { useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import {
  BodyText,
  Icon,
  type IconName,
  Image,
  theme,
  usePressableState,
} from '@sg/garnish';

import { type PaymentMethod, PaymentMethodType } from '../../types';
import { fallbackToSavedPaymentMethod, getCreditCardIcon } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const StripePaymentMethod = (props: StripePaymentMethodProps) => {
  const { name, imageBase64, icon, isDisabled, onPress } = props;
  const { paymentMethods, isGooglePay } = props;

  const { formatMessage } = useIntl();
  const { ref, linkStyles } = usePaymentMethodLink(isDisabled);

  // Show default/first saved payment method if valid and no payment method is selected from the SDK.
  const fallbackPaymentMethod = fallbackToSavedPaymentMethod(paymentMethods);
  const isValidFallback = fallbackPaymentMethod?.cardType !== PaymentMethodType.Unsupported; // prettier-ignore
  const fallbackName = isValidFallback ? fallbackPaymentMethod?.description : undefined; // prettier-ignore
  const fallbackIcon = isValidFallback ? getCreditCardIcon(fallbackPaymentMethod?.cardType) : undefined; // prettier-ignore

  if (!name && !fallbackName) {
    return (
      <View style={styles.messageWrapper}>
        <BodyText
          underline
          ref={ref}
          style={linkStyles}
          sizeMatch={['24']}
          disabled={isDisabled}
          accessibilityRole="button"
          accessibilityLabel={formatMessage(messages.selectPaymentOption)}
          onPress={onPress}
        >
          <FormattedMessage {...messages.selectPaymentOption} />
        </BodyText>
      </View>
    );
  }

  return (
    <View style={styles.messageWrapper}>
      {name ? (
        <BodyText style={styles.message} sizeMatch={['24']}>
          <FormattedMessage {...messages.payWith} />
        </BodyText>
      ) : null}

      <Pressable
        ref={ref}
        style={styles.linkWrapper}
        disabled={isDisabled}
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.changePaymentMethod)}
        onPress={onPress}
      >
        <StripePaymentMethodImage
          imageBase64={imageBase64}
          icon={isGooglePay ? 'IconGooglePay' : icon || fallbackIcon}
        />

        <BodyText
          numberOfLines={1}
          ellipsizeMode="tail"
          sizeMatch={['24']}
          style={linkStyles}
        >
          {name || fallbackName}
        </BodyText>
      </Pressable>
    </View>
  );
};

const StripePaymentMethodImage = (props: StripePaymentMethodImageProps) => {
  const { imageBase64, icon } = props;

  if (imageBase64) {
    return (
      <Image
        source={{ uri: `data:image/png;base64,${imageBase64}` }}
        contentFit="contain"
        style={styles.image}
      />
    );
  }

  return (
    <Icon
      name={icon ?? 'IconCreditCard'}
      color={theme.colors.SPINACH}
      width={ICON_SIZE}
      height={ICON_SIZE}
    />
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const usePaymentMethodLink = (isDisabled: boolean) => {
  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const linkStyles = [
    styles.message,
    styles.link,
    isInteracting && styles.linkInteracting,
    isDisabled && styles.linkDisabled,
  ];

  return { ref, linkStyles };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  payWith: {
    defaultMessage: 'Pay with',
    description: 'Bag | Payment method | Hint',
  },
  selectPaymentOption: {
    defaultMessage: 'Select payment option',
    description: 'Bag | Selected Payment Method | Select payment option',
  },
  changePaymentMethod: {
    defaultMessage: 'Change payment method',
    description: 'Bag | Selected Payment Method | Select another',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const ICON_SIZE = 32;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  messageWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    marginRight: theme.spacing['8'],
    paddingVertical: theme.spacing['4'],
  },
  message: {
    lineHeight: ICON_SIZE,
  },
  linkWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    borderBottomColor: theme.colors.SPINACH,
    borderBottomWidth: 2,
  },
  link: {
    color: theme.colors.SPINACH,
  },
  linkInteracting: {
    color: theme.colors.KALE_HOVER,
  },
  linkDisabled: {
    color: theme.colors.GRAY,
  },
  image: {
    width: ICON_SIZE,
    height: ICON_SIZE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type StripePaymentMethodProps = Readonly<{
  paymentMethods: readonly PaymentMethod[];
  name?: string;
  imageBase64?: string;
  icon?: IconName;
  isGooglePay: boolean;
  isDisabled: boolean;
  onPress: () => void;
}>;

type StripePaymentMethodImageProps = Readonly<{
  imageBase64?: string;
  icon?: IconName;
}>;
